import { apiClient } from 'utils/API';
import { Moralis } from 'moralis';
import { useMutation } from 'react-query';

export type MissionsCompleted = {
  completedTimes: number;
  name: string;
  points: number;
};

type PointsResponse = {
  points: string;
  userId: string;
  totalPoints: number;
  summary: MissionsCompleted[];
};

export const getPointsForUser = async (userId: string) => {
  const { data } = await apiClient.get<PointsResponse>(`/users/${userId}/points`);
  return data;
};

type CreatePairResponse = {
  pairId: string;
  userId: string;
  gribbleTokenId: number;
  breakerTokenId: number;
};

export const pairBreakerWithEgg = async (userId: string, breakerId: number, gribbleId: number) => {
  const { data } = await apiClient.post<CreatePairResponse>(`/users/${userId}/egg-breaker-pairs`, {
    breakerTokenId: breakerId,
    gribbleTokenId: gribbleId,
  });

  return data;
};

export const unpairBreakerWithEgg = async (userId: string, pairId: number) => {
  const { data } = await apiClient.delete<PointsResponse>(
    `/users/${userId}/egg-breaker-pairs/${pairId}`,
  );
  return data;
};

type EggBreakerPairResponse = {
  id: string;
  userId: string;
  gribbleTokenId: number;
  breakerTokenId: number;
  pairDate: string;
};

const transformBreakerPairResponse = (pairs: EggBreakerPairResponse[]) => {
  return pairs.map(({ id, gribbleTokenId, breakerTokenId }) => {
    return {
      id: parseInt(id, 10),
      gribbleId: gribbleTokenId,
      breakerId: breakerTokenId,
    };
  });
};

export const getBreakerPairs = async (userId: string) => {
  const { data } = await apiClient.get<EggBreakerPairResponse[]>(
    `/users/${userId}/egg-breaker-pairs`,
  );

  return transformBreakerPairResponse(data);
};

type EggPointApplyResponse = {
  userPoints: number;
  userId: string;
  eggsPoints: EggPointResponse[];
};
type EggPointResponse = {
  gribbleTokenId: number;
  points: number;
};

export const useFavoriteEgg = () => {
  return useMutation(
    async ({
      gribbleTokenId,
      userId,
      favoriteBefore,
    }: EggMutationInput & { favoriteBefore: boolean }) => {
      if (!favoriteBefore) {
        await favoriteEgg(userId, gribbleTokenId);
      } else {
        await unfavoriteEgg(userId, gribbleTokenId);
      }
    },
  );
};

export const useApplyUserPointsToEgg = () => {
  return useMutation(async ({ userId, pointsChanged }: EggPointsMutationInput) => {
    const { data } = await apiClient.post<EggPointApplyResponse>(`/users/${userId}/eggs`, {
      points: pointsChanged,
    });
    return { eggsPoints: data.eggsPoints, userPoints: data.userPoints };
  });
};

export type EggPointsMutationInput = {
  userId: string;
  pointsChanged: number;
};

export type EggMutationInput = {
  userId: string;
  gribbleTokenId: number;
};

export type UserMissionsListResponse = {
  id: string;
  title: string;
  description: string;
  image: string;
  reward: number;
  type: string;
  slug: string;
  completed: boolean;
  url: string;
};

export const getMissionsList = async (userId: string) => {
  const { data } = await apiClient.get<UserMissionsListResponse[]>(
    `/users/${userId}/achievements`,
    { headers: { 'Cache-Control': 'no-cache' } },
  );

  return [...data];
};

export const completeMission = async (userId: string, slug: string) => {
  const { data } = await apiClient.post(`/users/${userId}/achievements/${slug}`);
  return data;
};

export type EggPoint = {
  gribbleTokenId: number;
  points: number;
  favorite: boolean;
};

export const getEggPoints = async (userId: string, gribbleTokenIds: number[]) => {
  const { data } = await apiClient.get<EggPoint[]>(`users/${userId}/eggs`, {
    params: {
      gribbleTokenIds: JSON.stringify(gribbleTokenIds),
    },
  });

  return data;
};

export type MoralisContext = {
  isAuthenticated: boolean;
  user: Moralis.User | null;
};

export const setUserEthAccounts = async (ctx: MoralisContext, userId: string): Promise<boolean> => {
  if (!ctx.isAuthenticated || !ctx.user) {
    return false;
  }

  try {
    ctx.user.set('db_userId', userId);
    ctx.user.save();

    const ethAddress = ctx.user?.attributes.ethAddress;
    await apiClient.post(`/users/${userId}/ethAccounts/${ethAddress}`);
  } catch (e) {
    console.error(e);
    return false;
  }

  return true;
};

export const requestPasswordReset = async (email: string): Promise<boolean> => {
  try {
    await apiClient.post(`/users/send-reset-password`, {
      email: email,
    });
  } catch (e) {
    console.error(e);
    return false;
  }

  return true;
};

export const resetPassword = async (password: string, token: string): Promise<boolean> => {
  try {
    await apiClient.post(`/users/reset-password`, {
      password: password,
      token: token,
    });
  } catch (e) {
    console.error(e);
    return false;
  }

  return true;
};

export const favoriteEgg = async (userId: string, gribbleTokenId: number) => {
  try {
    await apiClient.post(`/users/${userId}/favorite-eggs/${gribbleTokenId}`);
  } catch (e) {
    console.error(e);
  }
};

export const unfavoriteEgg = async (userId: string, gribbleTokenId: number) => {
  try {
    await apiClient.delete(`/users/${userId}/favorite-eggs/${gribbleTokenId}`);
  } catch (e) {
    console.error(e);
  }
};

export const autoPairEgg = async (userId: string, strongest: boolean) => {
  try {
    await apiClient.post(`/users/${userId}/auto-pair-breakers-eggs?pairStrongestEggs=${strongest}`);
  } catch (e) {
    console.log(e);
  }
};

export type ReferalCodeResponse = {
  userId: string;
  referralCode: string;
};

export const referralCode = async (userId: string): Promise<string> => {
  const { data } = await apiClient.post<ReferalCodeResponse>(`/users/${userId}/referral-code`);
  return data.referralCode;
};

export type ReferralReward = {
  pointsAwarded: number;
  pointsReceived: number;
  mintSpotAwarded: number;
  mintspotReceived: number;
  pointsDetails: {
    pointsAwardAsReferree: number;
    pointsAwardAsReferrer: number;
    pointsReceivedAsReferree: number;
    pointsReceivedAsReferrer: number;
  };
};

export const referralRewards = async (userId: string): Promise<ReferralReward> => {
  const { data } = await apiClient.get<ReferralReward>(`/users/${userId}/referral-awards`);
  return data;
};

export type POAP = {
  id: string;
  imageUrl: string;
  isClaimed: boolean;
  tokenId: string;
};

export const getPOAPs = async (userId: string): Promise<POAP[]> => {
  const { data } = await apiClient.get<POAP[]>(`/users/${userId}/poaps`);
  return data;
};

export const claimPOAP = async (userId: string, poapId: string): Promise<POAP> => {
  const { data } = await apiClient.post<POAP>(`/users/${userId}/poaps/${poapId}/claim`);
  return data;
};

export type Gribble = {
  tokenId: string;
  name: string;
  description: string;
  externalUrl: URL;
  imageUrl: URL;

  traitBackground: number;
  traitMainColor1: string;
  traitMainColor1Title: string;
  traitMainColor2: string;
  traitMainColor2Title: string;
  traitMainColor3: string;
  traitMainColor3Title: string;
  traitMainColor4: string;
  traitMainColor4Title: string;
  traitMarkingsColor: string;
  traitMarkingsColorTitle: string;
  traitBodyType: string;
  traitHeadType: string;
  traitLegType: string;
  traitTextureType: string;
  traitMarkingsType: string;
  hatchStartDate: Date;
  hatchedDate?: Date;
};

export const getUserGribbles = async (userId: string, tokenIds: number[]): Promise<Gribble[]> => {
  const { data } = await apiClient.get(`/users/${userId}/gribbles`, {
    params: {
      tokenIds: JSON.stringify(tokenIds),
    },
  });
  return data.gribbles;
};
